<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { AltArrowLeftIcon, AltArrowRightIcon } from '@solar-icons/vue/linear'
import AppBadge from '../AppBadge/AppBadge.vue'
import AppButton from '../AppButton/AppButton.vue'
import type { SegmentOption } from './segmented-control-types'
import { vScrollFade } from '../v-scroll-fade'
import {
  SEGMENTED_SCALE,
  SEGMENTED_THUMB,
  SEGMENTED_THUMB_FACE,
  type SegmentedControlSize,
} from './segmented-control-scale'

const props = withDefaults(
  defineProps<{
    options: SegmentOption[]
    /** Selected option's value (v-model). */
    modelValue: string
    ariaLabel?: string
    stacked?: boolean
    /** Segments take their content width and scroll horizontally, instead of splitting the track evenly. */
    scroll?: boolean
    /** Accessible name for the "scroll left" edge affordance; required when `scroll` is set. */
    scrollPrevLabel?: string
    /** Accessible name for the "scroll right" edge affordance; required when `scroll` is set. */
    scrollNextLabel?: string
    /** Total rendered height on the control scale: `md` (default, 34px) · `sm` (24px). */
    size?: SegmentedControlSize
  }>(),
  {
    ariaLabel: undefined,
    stacked: false,
    scroll: false,
    scrollPrevLabel: undefined,
    scrollNextLabel: undefined,
    size: 'md',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const scale = computed(() => SEGMENTED_SCALE[props.size])

const buttons = ref<(HTMLButtonElement | null)[]>([])
const trackRef = ref<HTMLElement | null>(null)

const activeIndex = computed(() =>
  props.options.findIndex((option) => option.value === props.modelValue),
)

const thumbRect = ref<{ left: number; width: number } | null>(null)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

let resizeObserver: ResizeObserver | undefined

function measureOverflow() {
  const track = trackRef.value
  if (!track || !props.scroll) {
    canScrollPrev.value = false
    canScrollNext.value = false
    return
  }
  canScrollPrev.value = track.scrollLeft > 0
  canScrollNext.value = track.scrollLeft + track.clientWidth < track.scrollWidth - 1
}

function measure() {
  const activeButton = buttons.value[activeIndex.value]
  if (!trackRef.value || !activeButton) {
    thumbRect.value = null
  } else {
    thumbRect.value = { left: activeButton.offsetLeft, width: activeButton.offsetWidth }
  }
  measureOverflow()
}

function scrollByTrack(direction: 1 | -1): void {
  trackRef.value?.scrollBy({
    left: direction * trackRef.value.clientWidth * 0.8,
    behavior: 'smooth',
  })
}

function observeGeometry() {
  resizeObserver?.disconnect()
  if (typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => measure())
  if (trackRef.value) resizeObserver.observe(trackRef.value)
  for (const button of buttons.value) {
    if (button) resizeObserver.observe(button)
  }
}

const thumbStyle = computed(() => ({
  transform: `translateX(${thumbRect.value?.left ?? 0}px)`,
  width: `${thumbRect.value?.width ?? 0}px`,
  opacity: activeIndex.value < 0 || !thumbRect.value ? '0' : '1',
}))

onMounted(() => {
  measure()
  observeGeometry()
  document.fonts?.ready.then(() => measure())
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(
  () => [props.modelValue, props.options],
  async () => {
    await nextTick()
    measure()
    observeGeometry()
  },
  { deep: true },
)

function select(value: string) {
  emit('update:modelValue', value)
}

function onKeydown(event: KeyboardEvent, index: number) {
  let nextIndex: number | null = null
  if (event.key === 'ArrowRight') nextIndex = (index + 1) % props.options.length
  else if (event.key === 'ArrowLeft')
    nextIndex = (index - 1 + props.options.length) % props.options.length
  else if (event.key === 'Home') nextIndex = 0
  else if (event.key === 'End') nextIndex = props.options.length - 1
  if (nextIndex === null) return

  event.preventDefault()
  const next = props.options[nextIndex]
  if (!next) return
  select(next.value)
  const nextButton = buttons.value[nextIndex]
  nextButton?.focus()
  nextButton?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}
</script>

<template>
  <div
    role="radiogroup"
    :aria-label="ariaLabel"
    :class="[
      'bg-bg-surface',
      scale.rail,
      scroll ? 'flex w-full items-center gap-4' : 'inline-flex',
    ]"
  >
    <AppButton
      size="sm"
      v-if="scroll && canScrollPrev"
      variant="ghost"
      icon-only
      :start-icon="AltArrowLeftIcon"
      :aria-label="scrollPrevLabel"
      @click="scrollByTrack(-1)"
    />
    <div
      ref="trackRef"
      v-scroll-fade="{ axis: 'x' }"
      class="relative w-full"
      :class="[
        scroll
          ? 'flex min-w-0 overflow-x-auto scrollbar-hidden'
          : 'grid auto-cols-fr grid-flow-col',
      ]"
      @scroll="measureOverflow"
    >
      <span aria-hidden="true" :class="[SEGMENTED_THUMB, scale.thumb]" :style="thumbStyle">
        <span :class="[SEGMENTED_THUMB_FACE, scale.thumb]" />
      </span>
      <button
        v-for="(option, index) in options"
        :key="option.value"
        :ref="(el) => (buttons[index] = el as HTMLButtonElement | null)"
        type="button"
        role="radio"
        :aria-checked="option.value === modelValue"
        :aria-label="option.iconOnly ? option.label : undefined"
        :title="option.iconOnly ? option.label : undefined"
        :tabindex="option.value === modelValue ? 0 : -1"
        class="relative z-10 flex cursor-pointer select-none items-center justify-center gap-4 whitespace-nowrap font-medium outline-none transition-colors duration-base ease-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-blue"
        :class="[
          scale.cellRadius,
          scale.text,
          option.iconOnly ? scale.iconBox : scale.box,
          scroll ? 'shrink-0' : '',
          stacked ? 'flex-col' : ['icon-sm box-content flex-row', scale.height],
          option.value === modelValue
            ? 'text-text-primary'
            : 'text-text-secondary hover:text-text-primary',
        ]"
        @click="select(option.value)"
        @keydown="onKeydown($event, index)"
      >
        <component :is="option.icon" v-if="option.icon" class="shrink-0" />
        <template v-if="!option.iconOnly">{{ option.label }}</template>
        <AppBadge v-if="option.badge && option.badge > 0" :tone="option.badgeTone">{{
          option.badge
        }}</AppBadge>
      </button>
    </div>
    <AppButton
      size="sm"
      v-if="scroll && canScrollNext"
      variant="ghost"
      icon-only
      :start-icon="AltArrowRightIcon"
      :aria-label="scrollNextLabel"
      @click="scrollByTrack(1)"
    />
  </div>
</template>
